import { addMoney, compareMoney, isZero, mulMoney, percentOf, toUnits, type Tone } from '@elysian/ui';
import type { DocumentSummary, TaxCode } from '../../data/source';

export const iso = (d: Date) => d.toISOString().slice(0, 10);

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return iso(d);
}

export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);
}

export const shortDate = (s: string | null | undefined) =>
  s ? new Intl.DateTimeFormat('en-IE', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(`${s}T12:00:00Z`)) : '—';

export const longDate = (s: string | null | undefined) =>
  s ? new Intl.DateTimeFormat('en-IE', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(`${s}T12:00:00Z`)) : '—';

export type InvoiceFilter = 'all' | 'draft' | 'unpaid' | 'overdue' | 'paid' | 'credit' | 'void';

export function isOverdue(d: DocumentSummary, today: string): boolean {
  return (d.status === 'posted' || d.status === 'part_paid') && !!d.due_date && d.due_date < today && !isZero(d.outstanding);
}

export function matches(d: DocumentSummary, f: InvoiceFilter, today: string): boolean {
  switch (f) {
    case 'all': return true;
    case 'draft': return d.status === 'draft';
    // Credit notes sit on the same list (#46), but only invoices are unpaid, overdue or paid.
    case 'unpaid': return d.type !== 'sales_credit' && (d.status === 'posted' || d.status === 'part_paid') && compareMoney(d.outstanding, '0') > 0;
    case 'overdue': return d.type !== 'sales_credit' && isOverdue(d, today);
    case 'paid': return d.type !== 'sales_credit' && d.status === 'paid';
    case 'credit': return d.type === 'sales_credit';
    case 'void': return d.status === 'void';
  }
}

/** One pill per invoice: what someone scanning the list needs to know first. */
export function invoiceState(d: DocumentSummary, today: string): { tone: Tone; label: string } {
  if (d.status === 'draft') return { tone: 'draft', label: 'Draft' };
  if (d.status === 'void') return { tone: 'draft', label: 'Void' };
  if (d.type === 'sales_credit') return isZero(d.outstanding) ? { tone: 'ok', label: 'Fully used' } : { tone: 'info', label: 'Credit to use' };
  if (d.status === 'paid') return { tone: 'ok', label: 'Paid' };
  if (isOverdue(d, today)) {
    const late = daysBetween(d.due_date!, today);
    return { tone: late > 30 ? 'danger' : 'warn', label: `Overdue ${late} ${late === 1 ? 'day' : 'days'}` };
  }
  if (d.status === 'part_paid') return { tone: 'info', label: 'Part paid' };
  return { tone: 'info', label: d.due_date ? `Due ${shortDate(d.due_date)}` : 'Posted' };
}

export const SEND_STATUS: Record<string, { label: string; tone?: 'danger' }> = {
  queued: { label: 'Email queued' },
  sent: { label: 'Emailed' },
  delivered: { label: 'Delivered' },
  opened: { label: 'Opened' },
  bounced: { label: 'Email bounced', tone: 'danger' },
  failed: { label: 'Email failed', tone: 'danger' },
};

/** True when the text is a money amount the ledger will accept (up to 4 places). */
export function isAmount(s: string): boolean {
  try {
    toUnits(s);
    return s.trim() !== '';
  } catch {
    return false;
  }
}

export interface DraftLine {
  /** The item the line came from, if any. */
  product_code?: string;
  key: string;
  description: string;
  quantity: string;
  unit_price: string;
  account_code: string;
  tax_code: string;
}

/**
 * The editor's running totals, worked out exactly as ledger.save_draft does:
 * net = round(qty × price, 2), VAT = round(net × rate, 2), per line.
 * Lines with an unreadable quantity or price count as zero until fixed.
 */
export function draftTotals(lines: DraftLine[], codes: TaxCode[]) {
  const rate = (c: string) => codes.find((t) => t.code === c)?.rate ?? '0';
  const byCode = new Map<string, { net: string; tax: string }>();
  const rows = lines.map((l) => {
    const ok = isAmount(l.quantity) && isAmount(l.unit_price);
    const net = ok ? mulMoney(l.quantity, l.unit_price) : '0';
    const tax = ok ? percentOf(net, rate(l.tax_code)) : '0';
    const b = byCode.get(l.tax_code) ?? { net: '0', tax: '0' };
    byCode.set(l.tax_code, { net: addMoney(b.net, net), tax: addMoney(b.tax, tax) });
    return { key: l.key, net, tax };
  });
  const net = addMoney('0', ...rows.map((r) => r.net));
  const tax = addMoney('0', ...rows.map((r) => r.tax));
  return { rows, net, tax, gross: addMoney(net, tax), byCode };
}

// Merge fields the database accepts in template text (ledger._allowed_tokens).
export const MERGE_FIELDS = [
  'customer.name', 'customer.email', 'org.name', 'org.vat_number', 'org.phone',
  'invoice.number', 'invoice.date', 'invoice.due_date', 'invoice.total', 'invoice.reference', 'invoice.balance',
  'statement.as_at', 'statement.balance', 'statement.overdue', 'month', 'year', 'period_start', 'period_end', 'payment.link',
] as const;

export function unknownMergeFields(text: string): string[] {
  const found = [...text.matchAll(/\{\{\s*([a-z_.]+)\s*\}\}/g)].map((m) => m[1]!);
  return [...new Set(found.filter((f) => !(MERGE_FIELDS as readonly string[]).includes(f)))];
}

export function merge(text: string, values: Record<string, string>): string {
  return text.replace(/\{\{\s*([a-z_.]+)\s*\}\}/g, (all, k: string) => values[k] ?? all);
}

type Terms = { payment_terms_days?: number | null; payment_terms_basis?: 'invoice_date' | 'end_of_month' | null };

/** When something dated `date` is due under a customer's or supplier's terms (#32, as ledger.due_date_for). */
export function dueDateFor(date: string, c?: Terms | null): string {
  const days = c?.payment_terms_days ?? 30;
  if (c?.payment_terms_basis !== 'end_of_month') return addDays(date, days);
  const d = new Date(`${date}T00:00:00Z`);
  const monthEnd = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
  return addDays(monthEnd.toISOString().slice(0, 10), days);
}

/** "30 days" or "30 days from end of month". */
export function termsText(c?: Terms | null): string {
  const days = c?.payment_terms_days ?? 30;
  return `${days} ${days === 1 ? 'day' : 'days'}${c?.payment_terms_basis === 'end_of_month' ? ' from end of month' : ''}`;
}
